
import React from 'react';
import { Sun, Cloud, Clock } from 'lucide-react';
import { SunStatus } from '@/types/terrace';
import { cn } from '@/lib/utils';

interface SunStatusIndicatorProps {
  status: SunStatus;
  showLabel?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const SunStatusIndicator = ({ 
  status, 
  showLabel = true, 
  className,
  size = 'md' 
}: SunStatusIndicatorProps) => {
  const getStatusConfig = (status: SunStatus) => {
    switch (status) {
      case 'sunny':
        return {
          icon: Sun,
          label: 'Sunny',
          className: 'text-sunset-yellow bg-gradient-to-r from-peach-orange to-coral-pink',
          iconClassName: 'text-white',
          pulseClass: 'animate-pulse'
        };
      case 'soon':
        return {
          icon: Clock,
          label: 'Soon',
          className: 'text-sunset-dark bg-gradient-to-r from-sunset-yellow to-dusty-rose',
          iconClassName: 'text-white',
          pulseClass: 'animate-pulse'
        };
      case 'not-sunny':
      default:
        return {
          icon: Cloud,
          label: 'Not Sunny',
          className: 'text-sunset-medium bg-gray-100',
          iconClassName: 'text-sunset-medium',
          pulseClass: ''
        };
    }
  };

  const config = getStatusConfig(status);
  const Icon = config.icon;

  const sizeClasses = {
    sm: {
      container: 'px-2 py-1 text-xs',
      icon: 'h-3 w-3',
      gap: 'gap-1'
    },
    md: {
      container: 'px-3 py-1.5 text-sm',
      icon: 'h-4 w-4',
      gap: 'gap-2'
    },
    lg: {
      container: 'px-4 py-2 text-base',
      icon: 'h-5 w-5',
      gap: 'gap-2'
    }
  };

  const sizeConfig = sizeClasses[size];

  if (!showLabel) {
    return (
      <div className={cn(
        'rounded-full flex items-center justify-center',
        config.className,
        config.pulseClass,
        sizeConfig.container,
        className
      )}>
        <Icon className={cn(sizeConfig.icon, config.iconClassName)} />
      </div>
    );
  }

  return (
    <div className={cn(
      'inline-flex items-center rounded-full font-medium',
      config.className,
      config.pulseClass,
      sizeConfig.container,
      sizeConfig.gap,
      className
    )}>
      <Icon className={cn(sizeConfig.icon, config.iconClassName)} />
      <span className={config.iconClassName}>{config.label}</span>
    </div>
  );
};

export default SunStatusIndicator;
